import React from "react"
import { StaticQuery, graphql } from "gatsby"

// Breakpointed sizing configuration
// xxs: < 480px, xs: 480-576px, sm: 576-768px, lg: > 768px
// You can tune these values as needed.
const BAND_HEIGHT_XXS = 550
const IFRAME_HEIGHT_XXS = 700
const IFRAME_OFFSET_XXS = 140

const BAND_HEIGHT_XS = 635
const IFRAME_HEIGHT_XS = 900
const IFRAME_OFFSET_XS = 90

const BAND_HEIGHT_SM = 700
const IFRAME_HEIGHT_SM = 900
const IFRAME_OFFSET_SM = 100

const BAND_HEIGHT_LG = 1000
const IFRAME_HEIGHT_LG = 1000
const IFRAME_OFFSET_LG = 0

class InThePipelineCover extends React.Component {
  constructor(props) {
    super(props)
    this.iframeRef = React.createRef()
    this.descRef = React.createRef()
    this.state = { loading: true, isSmall: false, isNarrow: false, isXs: false, isXxs: false }
  }

  componentDidMount() {
    // The cover page posts {type: 'umap-rendered'} once Vega has drawn the points; that is when the
    // chart is really visible, not the iframe's load event (which fires before the 31k points render).
    this._onMessage = (e) => {
      if (e && e.data && e.data.type === 'umap-rendered') this.setState({ loading: false })
    }
    window.addEventListener('message', this._onMessage)
    // With server-side rendering the iframe can finish loading (and Vega can finish drawing) before React
    // hydrates, so neither onLoad nor the rendered message would be seen. Catch up explicitly.
    try {
      const iframe = this.iframeRef.current
      if (iframe && iframe.contentWindow) {
        const doc = iframe.contentDocument
        if (doc && doc.readyState === 'complete') this.handleIframeLoad()
        iframe.contentWindow.postMessage({ type: 'umap-ping' }, '*')
      }
    } catch (e) {
      // cross-origin guard; the fallback timer still clears the loader
    }
    // Determine if viewport is small (<=1024px) and keep it updated
    try {
      this._mq = window.matchMedia('(max-width: 1024px)')
      const update = () => this.setState({ isSmall: !!this._mq.matches }, () => {
        // Re-adjust iframe height when breakpoint changes
        this.adjustIframeHeight()
      })
      if (this._mq.addEventListener) {
        this._mq.addEventListener('change', update)
      } else if (this._mq.addListener) {
        this._mq.addListener(update)
      }
      update()
      // Track narrow screens (<768px) to align with Tailwind md breakpoint (md starts at 768px)
      this._mqMd = window.matchMedia('(max-width: 767px)')
      const updateMd = () => this.setState({ isNarrow: !!this._mqMd.matches }, () => {
        this.adjustIframeHeight()
      })
      if (this._mqMd.addEventListener) {
        this._mqMd.addEventListener('change', updateMd)
      } else if (this._mqMd.addListener) {
        this._mqMd.addListener(updateMd)
      }
      updateMd()
      // Track extra small screens (<=576px)
      this._mqXs = window.matchMedia('(max-width: 576px)')
      const updateXs = () => this.setState({ isXs: !!this._mqXs.matches }, () => {
        this.adjustIframeHeight()
      })
      if (this._mqXs.addEventListener) {
        this._mqXs.addEventListener('change', updateXs)
      } else if (this._mqXs.addListener) {
        this._mqXs.addListener(updateXs)
      }
      updateXs()
      // Track extra-extra small screens (<480px)
      this._mqXxs = window.matchMedia('(max-width: 479px)')
      const updateXxs = () => this.setState({ isXxs: !!this._mqXxs.matches }, () => {
        this.adjustIframeHeight()
      })
      if (this._mqXxs.addEventListener) {
        this._mqXxs.addEventListener('change', updateXxs)
      } else if (this._mqXxs.addListener) {
        this._mqXxs.addListener(updateXxs)
      }
      updateXxs()
    } catch (e) {
      // no-op on SSR
    }
  }

  componentWillUnmount() {
    if (this._onMessage) window.removeEventListener('message', this._onMessage)
    if (this._loadingTimer) clearTimeout(this._loadingTimer)
    if (this._resizeListener) {
      window.removeEventListener("resize", this._resizeListener)
    }
    if (this._mq) {
      const update = () => this.setState({ isSmall: !!this._mq.matches })
      if (this._mq.removeEventListener) {
        this._mq.removeEventListener('change', update)
      } else if (this._mq.removeListener) {
        this._mq.removeListener(update)
      }
    }
    if (this._mqMd) {
      const updateMd = () => this.setState({ isNarrow: !!this._mqMd.matches })
      if (this._mqMd.removeEventListener) {
        this._mqMd.removeEventListener('change', updateMd)
      } else if (this._mqMd.removeListener) {
        this._mqMd.removeListener(updateMd)
      }
    }
    if (this._mqXs) {
      const updateXs = () => this.setState({ isXs: !!this._mqXs.matches })
      if (this._mqXs.removeEventListener) {
        this._mqXs.removeEventListener('change', updateXs)
      } else if (this._mqXs.removeListener) {
        this._mqXs.removeListener(updateXs)
      }
    }
    if (this._mqXxs) {
      const updateXxs = () => this.setState({ isXxs: !!this._mqXxs.matches })
      if (this._mqXxs.removeEventListener) {
        this._mqXxs.removeEventListener('change', updateXxs)
      } else if (this._mqXxs.removeListener) {
        this._mqXxs.removeListener(updateXxs)
      }
    }
  }

  adjustIframeHeight = () => {
    const iframe = this.iframeRef.current
    if (!iframe) return
    try {
      const doc = iframe.contentDocument || iframe.contentWindow?.document
      if (!doc) return
      // Normalize default margins/padding inside the iframe to avoid unintended top gaps
      try {
        if (doc.documentElement && doc.documentElement.style) {
          doc.documentElement.style.margin = '0'
          doc.documentElement.style.padding = '0'
        }
        if (doc.body && doc.body.style) {
          doc.body.style.margin = '0'
          doc.body.style.padding = '0'
        }
        // Inject a minimal CSS reset to eliminate internal top gaps
        const head = doc.head || doc.getElementsByTagName('head')[0]
        if (head && !doc.getElementById('iframe-reset-style')) {
          const style = doc.createElement('style')
          style.id = 'iframe-reset-style'
          style.type = 'text/css'
          style.appendChild(doc.createTextNode(`
            html, body { margin: 0 !important; padding: 0 !important; height: 100% !important; }
            body > :first-child { margin-top: 0 !important; padding-top: 0 !important; }
            /* Common wrappers that add responsive padding/margins */
            #root, #app, #container, .container, .wrapper, .chart, .chart-container, .vega-embed, figure {
              margin-top: 0 !important;
              padding-top: 0 !important;
            }
            /* Reset any inline padding-top used for aspect ratio hacks */
            [style*="padding-top"] { padding-top: 0 !important; }
            /* Ensure SVG/canvas don't introduce extra inline spacing */
            svg, canvas { display: block; margin: 0 !important; }
          `))
          head.appendChild(style)
        }
      } catch (_) {
        // ignore style injection errors
      }
      const body = doc.body
      const html = doc.documentElement
      const contentHeight = Math.max(
        body?.scrollHeight || 0,
        body?.offsetHeight || 0,
        html?.clientHeight || 0,
        html?.scrollHeight || 0,
        html?.offsetHeight || 0
      )
      // Fixed heights by breakpoint tier
      let targetHeight
      const tierIframeHeight = this.state.isXxs
        ? IFRAME_HEIGHT_XXS
        : (this.state.isXs
          ? IFRAME_HEIGHT_XS
          : (this.state.isNarrow ? IFRAME_HEIGHT_SM : IFRAME_HEIGHT_LG))
      iframe.style.marginTop = '0px'
      targetHeight = tierIframeHeight
      if (targetHeight) {
        iframe.style.height = `${targetHeight}px`
      }
    } catch (e) {
      // Cross-origin guard (shouldn't happen for same-site content)
    }
  }

  handleIframeLoad = () => {
    if (this._loaded) return
    this._loaded = true
    // Auto-size the iframe to its content and keep it updated on resize
    this.adjustIframeHeight()
    this._resizeListener = () => this.adjustIframeHeight()
    window.addEventListener("resize", this._resizeListener)
    // Ask the chart whether it has already drawn (covers the case where the message fired before hydration)
    try { this.iframeRef.current.contentWindow.postMessage({ type: 'umap-ping' }, '*') } catch (e) { /* ignore */ }
    // Fallback: if the rendered message never arrives (old browser, blocked CDN), clear the loader anyway
    this._loadingTimer = setTimeout(() => this.setState({ loading: false }), 8000)
  }
  render() {
    // fixed height removed; container height is content-driven
    return (
      // band
      <div className="w-full bg-mediumgrey mb-10">
        {/* container */}
        <div className="max-w-6xl mt-4 mb-4 mx-auto flex flex-wrap content-start justify-end p-2">
          {/* title row */}
          <div className="w-full lg:w-full p-2 pt-8 lg:pr-8 relative z-50">
            {/* title and date */}
            <div className="w-full relative z-50">{this.props.children}</div>
            {/* cover */}
            <div
              className="w-full relative z-10"
              style={{
                // Clip any upward-shifted iframe so it doesn't overlap the title
                overflow: "hidden",
                touchAction: "pan-y",
                pointerEvents: this.state.isSmall ? 'none' : 'auto',
                // Fixed heights by breakpoint
                height: this.state.isXxs
                  ? `${BAND_HEIGHT_XXS}px`
                  : (this.state.isXs
                    ? `${BAND_HEIGHT_XS}px`
                    : (this.state.isNarrow ? `${BAND_HEIGHT_SM}px` : `${BAND_HEIGHT_LG}px`)),
              }}
            >
              {/* overlay description: always overlay to avoid pushing iframe down */}
              <div
                ref={this.descRef}
                className="z-10 w-full md:w-1/2 font-light italic text-sm text-gray-900 bg-white/80 pointer-events-none p-1 md:p-0 absolute md:top-0 md:left-0"
              >
                Each data point is one paragraph from the nearly 5,000 posts Derek Lowe has published on In the Pipeline since 2002. Semantic features are extracted from every paragraph and projected into two dimensions, so points that sit closer together are closer in meaning. Color marks the post's topic. Click a topic in the legend to isolate it, double click to reset, and hover over a point for its source.
              </div>
              {this.state.loading && (
                <div
                  className="absolute inset-0 z-20 flex items-center justify-center"
                  aria-live="polite"
                >
                  <div className="text-sm text-gray-700 italic bg-white/80 px-3 py-2 rounded">
                    Loading visualization…
                  </div>
                </div>
              )}
              <StaticQuery
                query={graphql`
                  query UmapCoverHtml {
                    file(
                      sourceInstanceName: { eq: "blog" }
                      relativePath: { eq: "24-years-in-the-pipeline-derek-lowe-topics-trends-turning-points/cover/umap_derek_lowe_blog_tag_1.html" }
                    ) {
                      publicURL
                    }
                  }
                `}
                render={(data) => {
                  const url = data && data.file && data.file.publicURL
                  if (!url) {
                    return (
                      <div className="text-sm text-gray-600">
                        Visualization file not found. Ensure it exists at
                        {" "}
                        <code>
                          content/blog/24-years-in-the-pipeline-derek-lowe-topics-trends-turning-points/cover/umap_derek_lowe_blog_tag_1.html
                        </code>
                        .
                      </div>
                    )
                  }
                  return (
                    <iframe
                      title="UMAP visualization"
                      ref={this.iframeRef}
                      src={url}
                      className="w-full border-0 absolute"
                      style={{
                        display: "block",
                        width: "100%",
                        border: 0,
                        overflow: "hidden",
                        zIndex: 0,
                        top: this.state.isXxs
                          ? `-${IFRAME_OFFSET_XXS}px`
                          : (this.state.isXs
                            ? `-${IFRAME_OFFSET_XS}px`
                            : (this.state.isNarrow ? `-${IFRAME_OFFSET_SM}px` : `-${IFRAME_OFFSET_LG}px`)),
                        // Force-disable interactivity on small screens so parent page can scroll
                        pointerEvents: this.state.isSmall ? 'none' : 'auto',
                      }}
                      scrolling="no"
                      loading="eager"
                      onLoad={this.handleIframeLoad}
                    />
                  )
                }}
              />
            </div>
          </div>
        </div>
      </div>
    )
  }
}

export default InThePipelineCover
